import { useEffect } from "react";

import type { AppSseEvent } from "@elation/api-client/src/stitch-web";

import { SERVICE_URL } from "@src/env";

import { appBus } from "../../events";
import { useSSE } from "./useSSE";

export interface AppSSEEventsOptions {
  enabled?: boolean;
}

type SupportedAppSseEventType =
  | "encounter.snapshot"
  | "agenda_item.created"
  | "agenda_item.updated"
  | "agenda_item.deleted"
  | "encounter.finalizing"
  | "encounter.completed"
  | "encounter.coding_updated"
  | "encounter.reopened"
  | "encounter.superseded_by_reopen"
  | "encounter.transcription_created"
  | "transcript.snapshot"
  | "transcript.item.created"
  | "transcription.started"
  | "transcription.progress"
  | "transcription.succeeded"
  | "transcription.failed";

type AppSSEEvent = Extract<AppSseEvent, { type: SupportedAppSseEventType }>;

type AppSSEEventMap = {
  [K in AppSSEEvent["type"]]: Extract<AppSSEEvent, { type: K }>;
};

/**
 * Subscribes to the authenticated actor's single multiplexed SSE stream and
 * exposes the derived state every consumer in the app needs:
 *
 * - **Active encounter** lifecycle (`finalizing` / `completed` / `reopened` /
 *   `superseded_by_reopen`) and **agenda item** CRUD for the optional
 *   `encounterId`. Encounter events for other ids on the same stream are
 *   filtered out so multiple encounter views don't cross-talk.
 *
 * The server exposes one channel per session (`GET /api/events`, credentials
 * identify the physician or staff). Browsers cap concurrent same-origin
 * connections (HTTP/1.1: 6) and SSE connections are long-lived, so the app
 * should open at most one stream and multiplex here and on the server.
 *
 * Encounter events are also re-emitted on the local `appBus` so
 * components that don't render under PatientLayout (e.g. the Transcript
 * panel) can subscribe without adding their own SSE connection.
 *
 * @param userId When `null`, the hook does not open a connection (e.g.
 *   physician context not ready yet). Must match the signed-in physician when
 *   set; used to reset stream state when switching users.
 */
export function useAppSSEEvents(
  userId: number | null,
  type: "physician" | "staff",
  options?: AppSSEEventsOptions,
) {
  const { enabled = true } = options ?? {};

  const queryKey =
    userId != null
      ? (["/api/events", userId] as const)
      : (["app-sse-disabled"] as const);

  const { isFallbackPolling } = useSSE<AppSSEEventMap>({
    url:
      type === "physician"
        ? `${SERVICE_URL}/api/events?physicianId=${userId}`
        : `${SERVICE_URL}/api/events?staffId=${userId}`,
    queryKey,
    enabled: enabled && userId != null,
    handlers: {
      "encounter.snapshot"(payload) {
        appBus.emit("encounter.snapshot", payload);
      },
      "agenda_item.created"(payload) {
        appBus.emit("agenda_item.created", payload);
      },
      "agenda_item.updated"(payload) {
        appBus.emit("agenda_item.updated", payload);
      },
      "agenda_item.deleted"(payload) {
        appBus.emit("agenda_item.deleted", payload);
      },
      "encounter.finalizing"(payload) {
        appBus.emit("encounter.finalizing", payload);
      },
      "encounter.completed"(payload) {
        appBus.emit("encounter.completed", payload);
      },
      "encounter.coding_updated"(payload) {
        appBus.emit("encounter.coding_updated", payload);
      },
      "encounter.reopened"(payload) {
        appBus.emit("encounter.reopened", payload);
      },
      "encounter.superseded_by_reopen"(payload) {
        appBus.emit("encounter.superseded_by_reopen", payload);
      },
      "encounter.transcription_created"(payload) {
        appBus.emit("encounter.transcription_created", payload);
      },
      "transcript.snapshot"(data) {
        appBus.emit("transcript.snapshot", data);
      },
      "transcript.item.created"(data) {
        appBus.emit("transcript.item.created", data);
      },
      "transcription.started"(data) {
        appBus.emit("transcription.started", data);
      },
      "transcription.progress"(data) {
        appBus.emit("transcription.progress", data);
      },
      "transcription.succeeded"(data) {
        appBus.emit("transcription.succeeded", data);
      },
      "transcription.failed"(data) {
        appBus.emit("transcription.failed", data);
      },
    },
  });

  useEffect(() => {
    appBus.emit("sse.fallback-polling", { isFallbackPolling });
  }, [isFallbackPolling]);
}
